import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '../common/Spinner';
import { Pill } from '../ui/Pill';
import { useProjectStatistics, useSubprojectStatistics } from '../../hooks/useProjects';
import { formatCurrency, formatHours } from '../../utils/format';
import { BILLING_LOCK_COLORS } from '../common/billingLockColors';
import { MoneyMetricLabel } from '../common/MoneyMetricLabel';
import {
  computeMarginPercent,
  formatMarginPercent,
  type MoneyMetricKey,
} from '../../utils/moneyGlossary';

interface Props {
  projectId: number;
  hasSubprojects: boolean;
  hoursBudget?: number | null;
  moneyBudget?: number | null;
  /**
   * Phase 72 Plan 02 — CZK-equivalent of `moneyBudget` when the Default
   * budget is a foreign currency (from `budgetLockService.deriveBudgetCzk`).
   * Used as the costs-vs-budget denominator instead of the raw
   * `moneyBudget` so a foreign-currency budget still compares like-for-like
   * against Total Costs (which is always CZK) — the bar's MEANING is
   * unchanged (D-05), only the denominator's currency resolution differs.
   */
  moneyBudgetCzk?: number | null;
  /**
   * Phase 72 Plan 02 — true while the budget period is still open (the CZK
   * equivalent above is a live recompute, not yet locked). Drives the
   * Provisional/Locked pill.
   */
  moneyBudgetProvisional?: boolean;
  /**
   * Phase 72 Plan 04 — the Default budget's currency (`Project.
   * moneyTotalCurrency`). The provisional/locked pill only renders for a
   * foreign-currency budget; a plain CZK budget needs no such marker (the
   * backend always resolves `moneyBudgetProvisional=false` for CZK, which
   * would otherwise be indistinguishable from a genuinely locked foreign
   * budget without this signal).
   */
  moneyBudgetCurrency?: 'CZK' | 'EUR' | 'USD';
}

type TabType = 'overall' | 'subprojects';

export default function ProjectStatisticsSection({
  projectId,
  hasSubprojects,
  hoursBudget,
  moneyBudget,
  moneyBudgetCzk,
  moneyBudgetProvisional,
  moneyBudgetCurrency,
}: Props) {
  const [activeTab, setActiveTab] = useState<TabType>('overall');
  const { data: stats, isLoading, error } = useProjectStatistics(projectId);
  const { data: subprojectStats, isLoading: isLoadingSubprojects } = useSubprojectStatistics(
    projectId,
    hasSubprojects && activeTab === 'subprojects'
  );

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Project Statistics</h2>
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Project Statistics</h2>
        <p className="text-sm text-red-700">Failed to load statistics</p>
      </div>
    );
  }

  // MKS-332 — the API omits every cost-side field for anyone who is not admin
  // or finance, so the cost rows, the money-budget bar (its numerator IS Total
  // Costs) and the Margin row are all built only when the data is there.
  const wholeCosts = stats.wholeReport.totalCosts;
  const hasCosts = wholeCosts !== undefined;
  // The subproject breakdown is a second request with its own redaction, so
  // it gets its own probe rather than borrowing `hasCosts`.
  const hasSubprojectCosts = subprojectStats?.grandTotal.totalCosts !== undefined;

  const rows: {
    label: string;
    metric?: MoneyMetricKey;
    whole: number;
    lastMonth: number;
    format: 'hours' | 'currency';
    color: 'black' | 'green' | 'red';
  }[] = [
    { label: 'Hours Approved', whole: stats.wholeReport.hours, lastMonth: stats.lastMonth.hours, format: 'hours', color: 'black' },
    { label: 'Earned', metric: 'earned', whole: stats.wholeReport.totalBilling, lastMonth: stats.lastMonth.totalBilling, format: 'currency', color: 'green' },
    { label: 'Invoiced', metric: 'invoiced', whole: stats.wholeReport.invoicedBilling, lastMonth: stats.lastMonth.invoicedBilling, format: 'currency', color: 'green' },
    // MKS-332 follow-up (Petra, 2026-10-16, item 2) — Earned · Invoiced ·
    // Paid; this surface stopped at Invoiced.
    { label: 'Paid', metric: 'paid', whole: stats.wholeReport.paidBilling ?? 0, lastMonth: stats.lastMonth.paidBilling ?? 0, format: 'currency', color: 'green' },
    { label: 'Extra Gains', whole: stats.wholeReport.extraGains, lastMonth: stats.lastMonth.extraGains, format: 'currency', color: 'green' },
    ...(hasCosts
      ? ([
          { label: 'Labor Costs', metric: 'cost' as const, whole: stats.wholeReport.laborCosts ?? 0, lastMonth: stats.lastMonth.laborCosts ?? 0, format: 'currency' as const, color: 'red' as const },
          { label: 'Expenses', metric: 'cost' as const, whole: stats.wholeReport.expenses ?? 0, lastMonth: stats.lastMonth.expenses ?? 0, format: 'currency' as const, color: 'red' as const },
          { label: 'Cost', metric: 'cost' as const, whole: wholeCosts ?? 0, lastMonth: stats.lastMonth.totalCosts ?? 0, format: 'currency' as const, color: 'red' as const },
        ])
      : []),
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Project Statistics</h2>

      {/* Tabs - only show if there are subprojects */}
      {hasSubprojects && (
        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex space-x-6">
            <button
              onClick={() => setActiveTab('overall')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overall'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overall
            </button>
            <button
              onClick={() => setActiveTab('subprojects')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'subprojects'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              By Subprojects
            </button>
          </nav>
        </div>
      )}

      {/* Budget Tracker */}
      {(hoursBudget != null || moneyBudget != null) && stats && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {hoursBudget != null && hoursBudget > 0 && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Hours Budget</span>
                <span className="text-sm font-mono text-gray-600">
                  {formatHours(stats.wholeReport.hours)} / {formatHours(hoursBudget)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    stats.wholeReport.hours / hoursBudget > 1 ? 'bg-red-500' :
                    stats.wholeReport.hours / hoursBudget > 0.9 ? 'bg-yellow-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${Math.min(100, (stats.wholeReport.hours / hoursBudget) * 100)}%` }}
                />
              </div>
              <div className="mt-1 text-xs text-gray-500 text-right">
                {((stats.wholeReport.hours / hoursBudget) * 100).toFixed(1)}% used
                {stats.wholeReport.hours > hoursBudget && (
                  <span className="text-red-700 ml-2">
                    (+{formatHours(stats.wholeReport.hours - hoursBudget)} over)
                  </span>
                )}
              </div>
            </div>
          )}
          {/* MKS-332: the bar's numerator IS Total Costs, so without cost
              access there is nothing to draw — hidden rather than shown at
              0 %, which would read as "no budget used". */}
          {moneyBudget != null && moneyBudget > 0 && hasCosts && (() => {
            // Phase 72 (currency-aware budgets, D-05) — the bar still
            // compares Total Costs (always CZK) vs the budget; for a
            // foreign-currency Default budget, moneyBudgetCzk is the
            // CZK-equivalent denominator so the ratio stays like-for-like.
            // The comparison's MEANING is unchanged — only the currency
            // resolution of the denominator differs.
            const effectiveBudget = moneyBudgetCzk ?? moneyBudget;
            // `hasCosts` above guarantees this is defined; the ?? 0 is for
            // the type narrowing TS cannot carry across the closure.
            const costsSoFar = wholeCosts ?? 0;
            // The pill only applies to a foreign-currency budget — a plain
            // CZK budget has no provisional/locked distinction to show.
            const isForeignBudget = !!moneyBudgetCurrency && moneyBudgetCurrency !== 'CZK';
            return (
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Money Budget</span>
                    {isForeignBudget && (
                      moneyBudgetProvisional ? (
                        <Pill
                          bg={BILLING_LOCK_COLORS.provisional.bg}
                          fg={BILLING_LOCK_COLORS.provisional.fg}
                          dot={BILLING_LOCK_COLORS.provisional.dot}
                        >
                          Provisional (open period)
                        </Pill>
                      ) : (
                        <Pill
                          bg={BILLING_LOCK_COLORS.locked.bg}
                          fg={BILLING_LOCK_COLORS.locked.fg}
                          dot={BILLING_LOCK_COLORS.locked.dot}
                        >
                          Locked
                        </Pill>
                      )
                    )}
                  </div>
                  <span className="text-sm font-mono text-gray-600">
                    {formatCurrency(costsSoFar)} / {formatCurrency(effectiveBudget)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      costsSoFar / effectiveBudget > 1 ? 'bg-red-500' :
                      costsSoFar / effectiveBudget > 0.9 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, (costsSoFar / effectiveBudget) * 100)}%` }}
                  />
                </div>
                <div className="mt-1 text-xs text-gray-500 text-right">
                  {((costsSoFar / effectiveBudget) * 100).toFixed(1)}% used
                  {costsSoFar > effectiveBudget && (
                    <span className="text-red-700 ml-2">
                      (+{formatCurrency(costsSoFar - effectiveBudget)} over)
                    </span>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Overall Statistics Tab */}
      {activeTab === 'overall' && (
        <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Overall project statistics table">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Metric
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Whole Report
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Month
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => {
                const colorClass = row.color === 'green' ? 'text-green-700' : row.color === 'red' ? 'text-red-700' : 'text-gray-900';
                return (
                  <tr key={row.label}>
                    <td className="px-4 py-2 text-sm text-gray-900">
                      {row.metric ? (
                        <MoneyMetricLabel metric={row.metric} label={row.label} />
                      ) : (
                        row.label
                      )}
                    </td>
                    <td className={`px-4 py-2 text-sm text-right font-mono ${colorClass}`}>
                      {row.format === 'currency' ? formatCurrency(row.whole) : formatHours(row.whole)}
                    </td>
                    <td className={`px-4 py-2 text-sm text-right font-mono ${colorClass}`}>
                      {row.format === 'currency' ? formatCurrency(row.lastMonth) : formatHours(row.lastMonth)}
                    </td>
                  </tr>
                );
              })}
              {/* MKS-332 Margin row (was Profit). Absent entirely without
                  cost access, since Margin is Earned − Cost. */}
              {stats.profit && (
                <tr className="bg-gray-50 font-medium">
                  <td className="px-4 py-2 text-sm text-gray-900">
                    <MoneyMetricLabel metric="margin" />
                  </td>
                  <td className="px-4 py-2 text-sm text-right font-mono">
                    <span className={stats.profit.wholeReport >= 0 ? 'text-green-700' : 'text-red-700'}>
                      {formatCurrency(stats.profit.wholeReport)}
                    </span>
                    <span className="text-gray-500 ml-2 text-xs">
                      {/* MKS-332: the ONE formula, (Earned − Cost) ÷ Earned. */}
                      {`(${formatMarginPercent(
                        computeMarginPercent(stats.wholeReport.totalBilling, wholeCosts),
                      )})`}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm text-right font-mono">
                    <span className={stats.profit.lastMonth >= 0 ? 'text-green-700' : 'text-red-700'}>
                      {formatCurrency(stats.profit.lastMonth)}
                    </span>
                    <span className="text-gray-500 ml-2 text-xs">
                      {`(${formatMarginPercent(
                        computeMarginPercent(
                          stats.lastMonth.totalBilling,
                          stats.lastMonth.totalCosts,
                        ),
                      )})`}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Subprojects Statistics Tab */}
      {activeTab === 'subprojects' && (
        <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Subproject statistics table">
          {isLoadingSubprojects ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : subprojectStats && subprojectStats.subprojects.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subproject
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hours
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <MoneyMetricLabel metric="earned" align="right" />
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <MoneyMetricLabel metric="invoiced" align="right" />
                  </th>
                  {/* MKS-332: cost and margin columns only when the API sent them. */}
                  {hasSubprojectCosts && (
                    <>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <MoneyMetricLabel metric="cost" align="right" />
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <MoneyMetricLabel metric="margin" align="right" />
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {subprojectStats.subprojects.map((sp) => (
                  <tr key={sp.projectId} className={`hover:bg-gray-50 ${(sp.depth ?? 0) > 0 ? 'bg-gray-50/50' : ''}`}>
                    <td className="px-4 py-2 text-sm">
                      <div style={{ paddingLeft: `${(sp.depth ?? 0) * 20}px` }} className="flex items-center">
                        {(sp.depth ?? 0) > 0 && (
                          <span className="text-gray-300 mr-2">└</span>
                        )}
                        {sp.projectName === '(Direct entries)' ? (
                          <span className="text-gray-500 italic">{sp.projectName}</span>
                        ) : (
                          <Link
                            to={`/projects/${sp.projectId}`}
                            className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
                          >
                            {sp.projectName}
                          </Link>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-sm text-right font-mono text-gray-900">
                      {formatHours(sp.hours)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right font-mono text-green-700">
                      {formatCurrency(sp.totalBilling)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right font-mono text-green-700">
                      {formatCurrency(sp.invoicedBilling)}
                    </td>
                    {hasSubprojectCosts && (
                      <>
                        <td className="px-4 py-2 text-sm text-right font-mono text-red-700">
                          {formatCurrency(sp.totalCosts ?? 0)}
                        </td>
                        <td className="px-4 py-2 text-sm text-right font-mono">
                          <span className={(sp.profit ?? 0) >= 0 ? 'text-green-700' : 'text-red-700'}>
                            {formatCurrency(sp.profit ?? 0)}
                          </span>
                          <span className="text-gray-500 ml-1 text-xs">
                            {`(${formatMarginPercent(computeMarginPercent(sp.totalBilling, sp.totalCosts))})`}
                          </span>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {/* Grand Total row */}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-4 py-2 text-sm text-gray-900">Total</td>
                  <td className="px-4 py-2 text-sm text-right font-mono text-gray-900">
                    {formatHours(subprojectStats.grandTotal.hours)}
                  </td>
                  <td className="px-4 py-2 text-sm text-right font-mono text-green-700">
                    {formatCurrency(subprojectStats.grandTotal.totalBilling)}
                  </td>
                  <td className="px-4 py-2 text-sm text-right font-mono text-green-700">
                    {formatCurrency(subprojectStats.grandTotal.invoicedBilling)}
                  </td>
                  {hasSubprojectCosts && (
                    <>
                      <td className="px-4 py-2 text-sm text-right font-mono text-red-700">
                        {formatCurrency(subprojectStats.grandTotal.totalCosts ?? 0)}
                      </td>
                      <td className="px-4 py-2 text-sm text-right font-mono">
                        <span className={(subprojectStats.grandTotal.profit ?? 0) >= 0 ? 'text-green-700' : 'text-red-700'}>
                          {formatCurrency(subprojectStats.grandTotal.profit ?? 0)}
                        </span>
                        <span className="text-gray-500 ml-1 text-xs">
                          {`(${formatMarginPercent(
                            computeMarginPercent(
                              subprojectStats.grandTotal.totalBilling,
                              subprojectStats.grandTotal.totalCosts,
                            ),
                          )})`}
                        </span>
                      </td>
                    </>
                  )}
                </tr>
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-gray-500 py-4">No subproject data available.</p>
          )}
        </div>
      )}
    </div>
  );
}
